import { hasSupabasePublicConfig, supabase } from './supabase';
import type { CassemsCartLine, CassemsCartTotals } from '../contexts/CassemsCartContext';
import type { CassemsPreferenciaPagamento } from './cassemsWhatsappPedido';

export type CassemsClientePayload = {
  nome: string;
  telefone: string;
  cpf: string;
  endereco: string;
  setor: string;
};

export type NotifyCassemsPedidoResult =
  | {
      ok: true;
      pedidoId?: string;
      clientRequestId: string;
      warning?: string;
      duplicate?: boolean;
    }
  | {
      ok: false;
      clientRequestId: string;
      message: string;
      pedidoId?: string;
    };

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export function buildCassemsClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cassems-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function notifyCassemsPedidoApi(params: {
  cliente: CassemsClientePayload;
  lines: CassemsCartLine[];
  totais: CassemsCartTotals;
  preferenciaPagamento: CassemsPreferenciaPagamento;
  pdfBase64?: string;
  pdfFileName?: string;
  clientRequestId: string;
}): Promise<NotifyCassemsPedidoResult> {
  if (!hasSupabasePublicConfig) {
    return {
      ok: false,
      clientRequestId: params.clientRequestId,
      message:
        'Este build do site está sem a configuração pública do Supabase. A página abriu, mas a automação não consegue registrar o pedido.',
    };
  }

  const token = import.meta.env.VITE_CASSEMS_SUBMIT_TOKEN?.trim();
  const itens = params.lines.map((l) => ({
    id: l.id,
    nome: l.nome,
    quantidade: l.quantidade,
    precoUnitario: l.precoUnitComDesconto,
    subtotal: Math.round(l.precoUnitComDesconto * l.quantidade * 100) / 100,
  }));

  const body = {
    ...(token ? { token } : {}),
    clientRequestId: params.clientRequestId,
    campanha: 'cassems',
    preferenciaPagamento: params.preferenciaPagamento,
    cliente: params.cliente,
    itens,
    totais: {
      subtotalCheio: Math.round(params.totais.subtotalCheio * 100) / 100,
      descontoPercentual: params.totais.descontoPercentual,
      subtotalPrecosPromo: Math.round(params.totais.subtotalPrecosPromo * 100) / 100,
      valorDescontoCombo: Math.round(params.totais.valorDescontoCombo * 100) / 100,
      paresCombo: params.totais.paresCombo,
      comboDetalhe: params.totais.comboDetalhe,
      totalComDesconto: Math.round(params.totais.subtotalComDesconto * 100) / 100,
    },
    ...(params.pdfBase64
      ? {
          pdf: {
            base64: params.pdfBase64,
            fileName: params.pdfFileName || 'lunins-encomenda-cassems.pdf',
          },
        }
      : {}),
  };

  const { data, error } = await supabase.functions.invoke('cassems-encomenda-notify', { body });

  if (error) {
    let message = error.message || 'Falha ao enviar notificação automática.';
    if (/Failed to send a request to the Edge Function/i.test(message)) {
      message =
        'Não foi possível contatar o servidor (notificação automática para a equipe). O PDF já foi baixado — use o WhatsApp do Demétrio nesta tela para enviar o resumo.';
    }
    return { ok: false, message, clientRequestId: params.clientRequestId };
  }
  const j = data as { ok?: boolean; error?: string; warning?: string; pedidoId?: string; duplicate?: boolean };
  if (j?.ok === false || j?.error) {
    return {
      ok: false,
      message: j.error || 'Servidor recusou o pedido',
      pedidoId: j.pedidoId,
      clientRequestId: params.clientRequestId,
    };
  }
  return {
    ok: true,
    pedidoId: j?.pedidoId,
    warning: j?.warning,
    duplicate: j?.duplicate,
    clientRequestId: params.clientRequestId,
  };
}

/**
 * Notificação interna do pedido Cassems.
 * Default: ligada. Só desliga se VITE_CASSEMS_EDGE_NOTIFY=0.
 */
export function isCassemsEdgeNotifyEnabled(): boolean {
  return import.meta.env.VITE_CASSEMS_EDGE_NOTIFY !== '0';
}

export async function notifyCassemsPedidoWithRetry(
  params: Omit<Parameters<typeof notifyCassemsPedidoApi>[0], 'clientRequestId'> & { clientRequestId?: string }
): Promise<NotifyCassemsPedidoResult> {
  if (!isCassemsEdgeNotifyEnabled()) {
    return {
      ok: true,
      clientRequestId: params.clientRequestId || buildCassemsClientRequestId(),
      warning: 'Automação desativada neste build.',
    };
  }

  const clientRequestId = params.clientRequestId || buildCassemsClientRequestId();
  let lastResult: NotifyCassemsPedidoResult | null = null;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    lastResult = await notifyCassemsPedidoApi({ ...params, clientRequestId });
    if (lastResult.ok) return lastResult;

    const shouldRetry =
      /contatar o servidor|edge function|failed to send a request|network/i.test(lastResult.message);
    if (!shouldRetry || attempt === 2) return lastResult;

    await sleep(700 * (attempt + 1));
  }

  return (
    lastResult || {
      ok: false,
      clientRequestId,
      message: 'Falha ao registrar o pedido automaticamente.',
    }
  );
}
